import { useCallback, useEffect, useState } from 'react'

const KEY = 'acecast.cinema'
/** Below this width the player already spans the page, so the mode is moot. */
const WIDE = '(min-width: 760px)'

function readStored(): boolean {
  try {
    return localStorage.getItem(KEY) === '1'
  } catch {
    return false
  }
}

/**
 * A YouTube-style theatre mode: the player breaks out of the page column and
 * fills the viewport width, without going fullscreen.
 */
export function useCinemaMode() {
  const [wide, setWide] = useState(() => window.matchMedia(WIDE).matches)
  const [enabled, setEnabled] = useState(readStored)

  useEffect(() => {
    const query = window.matchMedia(WIDE)
    const onChange = () => setWide(query.matches)
    query.addEventListener('change', onChange)
    return () => query.removeEventListener('change', onChange)
  }, [])

  /**
   * A full-bleed element sized with 100vw ends up wider than the content area
   * whenever a vertical scrollbar is present, because vw counts the scrollbar.
   * Measure the difference and let CSS subtract it.
   */
  useEffect(() => {
    const measure = () => {
      const width = window.innerWidth - document.documentElement.clientWidth
      document.documentElement.style.setProperty('--scrollbar', `${Math.max(0, width)}px`)
    }
    measure()
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [enabled])

  const toggle = useCallback(() => {
    setEnabled((current) => {
      const next = !current
      try {
        localStorage.setItem(KEY, next ? '1' : '0')
      } catch {
        // Preference only; not worth failing over.
      }
      return next
    })
  }, [])

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.ctrlKey || event.metaKey || event.altKey) return

      // Never steal keys while the user is typing a link or a password.
      const target = event.target as HTMLElement | null
      const tag = target?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || target?.isContentEditable) return

      if (event.key === 't' || event.key === 'T') {
        event.preventDefault()
        toggle()
      } else if (event.key === 'Escape' && enabled) {
        setEnabled(false)
        try {
          localStorage.setItem(KEY, '0')
        } catch {
          /* ignore */
        }
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [enabled, toggle])

  return { cinema: enabled && wide, available: wide, toggle }
}
